import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { changeSessionState, isFinishedState, isSessionConsumerInputNeededState } from './session.slice';
import { ConsumerInputNeededState, SessionState } from '../../types/session';
import { startSessionSuccess } from '../actions/session.actions';

export type Step =
	| 'LOADING'
	| 'BANK_SELECTION'
	| 'FORM_INPUT'
	| 'REDIRECTION'
	| 'ACCOUNT_SELECTION'
	| 'PROCESSING'
	| 'FINISHED'
	| 'ABORTED'
	| 'EXCEPTION';

export interface State {
	current: Step;
	history: Array<Step>;
}

const initialState: State = {
	current: 'LOADING',
	history: []
};

export const stepSlice = createSlice({
	name: 'step',
	initialState,
	reducers: {
		changeStep(state, action: PayloadAction<Step>) {
			const step = action.payload;
			state.history.push(state.current);
			state.current = step;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(changeSessionState, (state, action) => {
			const session = action.payload;

			if (isFinishedState(session)) {
				return {
					current: 'FINISHED',
					history: state.history.concat(state.current)
				};
			}

			if (isSessionConsumerInputNeededState(session)) {
				return {
					current: getActiveSessionStep(session),
					history: state.history.concat(state.current)
				};
			}
		});

		builder.addCase(startSessionSuccess, (state, action) => {
			const session = action.payload;
			const step = getSessionStep(session);
			return {
				current: step,
				history: state.history.concat(state.current)
			};
		});
	}
});

const getSessionStep = (session: SessionState): Step => {
	if (isSessionConsumerInputNeededState(session)) {
		return getActiveSessionStep(session);
	}

	return session.status;
};

const getActiveSessionStep = (session: ConsumerInputNeededState): Step => {
	const type = session.result.type;
	switch (type) {
		case 'form':
			return 'FORM_INPUT';
		case 'redirect':
			return 'REDIRECTION';
		case 'bank_selection':
			return 'BANK_SELECTION';
		case 'account_selection':
			return 'ACCOUNT_SELECTION';
		default: {
			throw new Error('Unsupported session state type: ' + type);
		}
	}
};

export const { changeStep } = stepSlice.actions;

export const selectCurrentStep = (state: RootState): Step => {
	return state.step.current;
};

export const selectPreviousStep = (state: RootState): Step | undefined => {
	const history = state.step.history;
	return history[history.length - 1];
};
